import React from 'react';
import _ from 'lodash';
import { useMutation } from 'react-fetching-library';
import { Card, CardHeader, CardContent, Modal, IconButton, Button, Stack, LinearProgress, Typography } from '@mui/material';
import { GridToolbar, plPL } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import toast from 'react-hot-toast';
import { matchBankStatementsWithOrdersAction, submitTransactionAction } from '../../../services/request';

import EditTransaction from './EditTransaction';
import SplitTransaction from './SplitTransaction';
import MatchInformation from './MatchInformation';
import MatchOrder from './MatchOrder';
import { useSelector } from 'react-redux';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const columns = [
  { field: 'amount', headerName: 'Kwota', flex: 0.1, valueGetter: params => params.row.amount.amount || params.row.amount },
  { field: 'entry_date', headerName: 'Data', flex: 0.1 },
  { field: 'contractor', headerName: 'Kontrahent', flex: 0.3 },
  { field: 'title', headerName: 'Tytuł płatności', flex: 0.6 },
  { field: 'bestMatch', headerName: 'Dopasowane zamówienie', flex: 0.2 },
  { field: 'guardian', headerName: 'Opiekun', flex: 0.1 },
  { field: 'participant', headerName: 'Uczestnik', flex: 0.1 },
  { field: 'weight_percentage', headerName: '%', flex: 0.1, valueGetter: params => _.isNil(params.row.weight_percentage) ? '' : (params.row.weight_percentage * 100).toFixed(2) },
  { field: 'order_name', headerName: 'Zamówienie', flex: 0.1 },
  { field: 'document_name', headerName: 'Dokument', flex: 0.1 }

];

const collectColumns = [
  { field: 'amount', headerName: 'Kwota', flex: 0.1, valueGetter: params => params.row.amount.amount || params.row.amount },
  { field: 'entry_date', headerName: 'Data', flex: 0.1 },
  { field: 'iba', headerName: 'Nr. IKB', flex: 0.1 },
  { field: 'contractor', headerName: 'Kontrahent', flex: 0.3 },
  { field: 'title', headerName: 'Tytuł płatności', flex: 0.6 },
  { field: 'bestMatch', headerName: 'Dopasowane zamówienie', flex: 0.2 },
  { field: 'guardian', headerName: 'Opiekun', flex: 0.1 },
  { field: 'participant', headerName: 'Uczestnik', flex: 0.1 },
  { field: 'weight_percentage', headerName: '%', flex: 0.1, valueGetter: params => _.isNil(params.row.weight_percentage) ? '' : (params.row.weight_percentage * 100).toFixed(2) },
  { field: 'order_name', headerName: 'Zamówienie', flex: 0.1 },
  { field: 'document_name', headerName: 'Dokument', flex: 0.1 }
];

const Transactions = ({ open, handleClose, transactions, setTransactions, collect }) => {
  const { seasonYear } = useSelector(state => state.app);
  const { mutate, loading } = useMutation(matchBankStatementsWithOrdersAction);
  const { mutate: mutateTransaction, loading: loadingTransaction } = useMutation(submitTransactionAction);
  const [selectionModel, setSelectionModel] = React.useState([]);
  const [openSecondModal, setOpenSecondModal] = React.useState(false);
  const [secondModalType, setSecondModalType] = React.useState(0);
  const [transaction, setTransaction] = React.useState({});
  const [matchInfo, setMatchInfo] = React.useState({});
  const [allMatches, setAllMatches] = React.useState([]);

  const handleOpenSecondModal = () => {
    const selectedTransaction = transactions.find(transaction => transaction.id === selectionModel[0]);
    setTransaction(selectedTransaction);
    setOpenSecondModal(true);
  };

  const handleMatchSelected = async () => {
    if (selectionModel.length > 0) {
      const selectedTransaction = transactions.find(transaction => transaction.id === selectionModel[0]);
      const { error: mutateError, payload } = await mutate({ 'transactions': selectedTransaction, collect, season_year: seasonYear });
      if (!mutateError) {
        if (_.isNil(payload.results[0].best_match) && payload.results[0].other_matches.length === 0) {
          toast('Nie znaleziono dopasowań');
        } else {
          setAllMatches([payload.results[0].best_match, ...payload.results[0].other_matches]);
          setSecondModalType(4);
          handleOpenSecondModal();
        }
      }
    } else {
      toast('Wybierz transakcje');
    }
  };

  const handleMatchAll = async () => {

    const { error: mutateError, payload } = await mutate({ transactions, collect, season_year: seasonYear });
    console.log(payload);
    if (!mutateError) {
      for (const element of payload.results) {
        setTransactions(prev => prev.map(t => {
          if (t.id !== element.bank_statement_id) {
            return t;
          }

          if (_.isNil(element.best_match)) {
            return { ...t, bestMatch: 'Brak dopasowania' };
          }
          if (element.best_match.mismatched_season) {
            return { ...t, bestMatch: 'Pominięte - wpłata na IKB z innego sezonu niż wybrany' };
          }

          return {
            ...t,
            order_id: element.best_match.order.id,
            bestMatch: element.best_match.order.identifier,
            guardian: element.best_match.order.guardian_name,
            participant: element.best_match.order.participant_name,
            weight_percentage: element.best_match.weight_percentage,
            order_name: element.best_match.order_name,
            document_name: element.best_match.document_name
          };

        }));
      }

      setMatchInfo({
        'mismatched': payload.mismatched,
        'created_receipts': payload.created_receipts,
        'mismatched_season': payload.mismatched_season
      });
      setSecondModalType(3);
      handleOpenSecondModal();
    }

  };

  const handleCloseSecondModal = () => {
    setOpenSecondModal(false);
    setSecondModalType(0);
  };

  const handleDeleteTransaction = async () => {
    if (selectionModel.length > 0) {
      const { payload, error } = await mutateTransaction(selectionModel[0], 'DELETE', []);
      if (!error) {
        setTransactions(prev => prev.filter(transaction => transaction.id !== selectionModel[0]));
      }
    } else {
      toast('Wybierz transakcje');
    }
  };

  const handleEditTransaction = () => {
    if (selectionModel.length > 0) {
      setSecondModalType(1);
      handleOpenSecondModal();
    } else {
      toast('Wybierz transakcje');
    }
  };

  const handleSplitTransaction = () => {
    if (selectionModel.length > 0) {
      setSecondModalType(2);
      handleOpenSecondModal();
    } else {
      toast('Wybierz transakcje');
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setSelectionModel([]);
          handleClose();
        }}
      >
        <Card sx={{ ...style, width: '96vw', height: '96vh' }}>
          <CardHeader
            title="Transakcje"
            action={
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectionModel([]);
                  handleClose();
                }}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent sx={{ height: '80%' }}>
            <DataGridPro
              rows={transactions}
              columns={collect ? collectColumns : columns}
              pagination
              pageSize={100}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              checkboxSelection={false}
              components={{ Toolbar: GridToolbar }}
              localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
              onSelectionModelChange={newSelection => {
                setSelectionModel(newSelection);
              }}
              selectionModel={selectionModel}
            />
            {loading
              ? <Stack sx={{ height: '20%' }} justifyContent="center">
                <LinearProgress />
                <Typography variant="subtitle2" textAlign="center" sx={{ mt: 1 }}>Dopasowywanie...</Typography>
              </Stack>
              : <Stack sx={{ height: '20%' }} direction="row" alignItems="center" spacing={3}>
                <Button variant="outlined" onClick={handleMatchSelected}>Dopasuj dla zaznaczonego</Button>
                <Button variant="outlined" onClick={handleMatchAll}>Dopasuj wszystkie</Button>
                <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={handleEditTransaction}>Edytuj pozycje</Button>
                <Button variant="outlined" color="secondary" onClick={handleSplitTransaction}>Rozdziel pozycje</Button>
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteTransaction}>Usuń pozycje</Button>
              </Stack>
            }
          </CardContent>
        </Card>
      </Modal>
      {secondModalType === 1
        ? <EditTransaction open={openSecondModal} handleClose={handleCloseSecondModal} transaction={transaction} setTransactions={setTransactions} />
        : secondModalType === 2
          ? <SplitTransaction open={openSecondModal} handleClose={handleCloseSecondModal} transaction={transaction} setTransactions={setTransactions} collect={collect} />
          : secondModalType === 3
            ? <MatchInformation open={openSecondModal} handleClose={handleCloseSecondModal} matchInfo={matchInfo} />
            : secondModalType === 4
              ? <MatchOrder open={openSecondModal} handleClose={handleCloseSecondModal} orders={allMatches} transaction={transaction} setTransactions={setTransactions} collect={collect} />
              : null}
    </>
  );
};

export default Transactions;
